import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DOCS_URL, openDocsInNewTab } from "@/lib/openDocs";

interface Props {
  /**
   * Extra classes appended to the shared control styling, for a shell whose
   * header chrome differs. All three shells currently use the same white
   * header with gray-300 bordered controls, so this is normally unset.
   */
  className?: string;
}

/**
 * Documentation launcher shown in every shell header (TASK-4770).
 *
 * The docs portal moved from the retired public host docs.ddklogistic.com to
 * `/docs/` on this origin, where nginx serves it as a static Docusaurus site
 * behind an `auth_request` that validates the `docs_access` cookie. It is NOT
 * a react-router route, so this must stay a plain anchor: a client-side
 * <Link> would hand the path to the SPA router and render a 404.
 *
 * Clicking mints the cookie first, otherwise an already-signed-in user hits
 * the `auth_request` 401 and is bounced to `/login?next=/docs/` — asked to log
 * in again, in a new tab with no context, which reads as a broken feature.
 *
 * POPUP BLOCKING — the load-bearing detail. Browsers only allow `window.open`
 * while the stack is still the user's click. `await mintDocsSession()` then
 * `window.open(...)` runs a macrotask later, by which time the gesture has
 * expired and the tab is silently blocked — strictly worse than the login
 * redirect it was meant to fix. So the tab is opened SYNCHRONOUSLY here, in
 * the same tick as the click, and only its `location` is set once the mint
 * settles. A blank tab briefly showing is the cost of never being blocked.
 *
 * NO `noopener` IN THE FEATURES STRING — verified in a real browser, not just
 * in jsdom. `window.open(url, "_blank", "noopener")` returns `null` BY SPEC:
 * withholding the handle is precisely what `noopener` does. Passing it here
 * meant the handle was always null, the null-branch fell back to
 * `window.location`, and the click navigated the CURRENT tab away from the
 * dispatcher's work — the opposite of the intent, and invisible to a unit test
 * that mocks `window.open` into returning a fake tab. The opener relationship
 * is instead severed manually in `openDocsInNewTab`, which is equivalent for security here
 * (same-origin target) and keeps the handle we need to navigate the new tab.
 *
 * Degrades gracefully: whatever the mint does — network error, 401, or a popup
 * blocker that genuinely returned null — the user always ends up at `/docs/`.
 * The worst case is exactly today's behaviour (the login redirect), never a
 * dead click.
 */
export function DocsLink({ className }: Props) {
  const { t } = useTranslation();
  const label = t("nav.documentation");

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    // Let the browser handle modified clicks (ctrl/cmd/middle-click "open in
    // new tab", shift for a new window) natively. Those bypass our handler's
    // tab entirely, so hijacking them would open two tabs.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    // Synchronous — still inside the click gesture (see `openDocsInNewTab`).
    openDocsInNewTab(DOCS_URL);
  }

  return (
    <a
      href={DOCS_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-label={label}
      title={label}
      className={
        className ??
        "flex items-center justify-center rounded-lg border border-gray-300 p-1 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900"
      }
    >
      <BookOpen size={16} aria-hidden="true" />
    </a>
  );
}
